import { type RequestQuery, useApiQuery } from '@vrplatform/data';
import { sortBy } from '@vrplatform/utils';
import { useGuestBalancesTableFilter } from './useGuestBalancesTableFilter';
import {
  formatGuestBalancesRow,
  type GuestBalancesTableQueryRow,
  type GuestBalancesTableQuerySorting,
} from './useGuestBalancesTableQuery.utils';

export type { GuestBalancesTableQueryRow, GuestBalancesTableQuerySorting };
export { formatGuestBalancesRow };

export const STORYBOOK_GUEST_BALANCES_TABLE_QUERY_KEY = 'guest-balances-table';

export const getGuestBalancesQueryParams = (
  filter: ReturnType<typeof useGuestBalancesTableFilter>['filter']
): RequestQuery<'get:/reports/guest-balances'> => {
  return {
    date: filter.endDate,
    occupancyStatus: filter.occupancyStatus,
    channels: filter.channels?.join(',') ?? undefined,
  };
};

export const useGuestBalancesTableQuery = (
  sorting: GuestBalancesTableQuerySorting
) => {
  const { filter } = useGuestBalancesTableFilter();

  return useApiQuery(
    async ($api, signal, args) => {
      const result = await $api.GET('/reports/guest-balances', {
        params: {
          query: getGuestBalancesQueryParams(args.filter),
        },
        signal,
      });

      if (result.error) throw result.error;

      return {
        rows: sortBy(
          result.data.data.map((row) => formatGuestBalancesRow(row, sorting)),
          'name'
        ),
      };
    },
    {
      queryGroup: 'reports',
      uniqueQueryKey: 'guest-balances-report',
      storybookQueryKey: STORYBOOK_GUEST_BALANCES_TABLE_QUERY_KEY,
      queryKeys: ['journalEntries', 'reports'],
      variables: {
        filter,
        sorting,
      },
    }
  );
};
